import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  FormControl,
  Button,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Checkbox,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { FiberManualRecord as DotIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';
import { useNavigate } from 'react-router-dom';

// Project import
import { gridSpacing } from 'config.js';
import { candidatesData } from '../../Data/Kontak/Candidate';
import StatusConfirmationModal from 'component/StatusConfirmationModal';

const CandidatePage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusModal, setStatusModal] = useState({ open: false, type: '', data: null });
  const [statusFilter, setStatusFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewTab, setViewTab] = useState(0); // 0 = List, 1 = Kanban
  const [selectedIds, setSelectedIds] = useState(new Set());
  const navigate = useNavigate();

  // API Functions
  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://apidashboard.eurekagroup.id/candidates', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      // Map _id to id for frontend compatibility
      const mappedData = (result.data || result).map(candidate => ({
        ...candidate,
        id: candidate._id
      }));
      setData(mappedData);
      console.log('Candidates loaded:', result);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      // Fallback to static data if API fails
      setData(candidatesData);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Warning!',
        text: 'Gagal memuat data dari server, menggunakan data lokal'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidatesByStatus = async (status) => {
    try {
      setLoading(true);
      const response = await fetch(`https://apidashboard.eurekagroup.id/candidates/status/${status}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      // Map _id to id for frontend compatibility
      const mappedData = (result.data || result).map(candidate => ({
        ...candidate,
        id: candidate._id
      }));
      setData(mappedData);
      console.log(`Candidates with status ${status}:`, result);
    } catch (error) {
      console.error('Error fetching candidates by status:', error);
      // Fallback to filtering local data
      const filteredData = candidatesData.filter(candidate => candidate.applicationStatus === status);
      setData(filteredData);
    } finally {
      setLoading(false);
    }
  };

  const deleteCandidate = async (id) => {
    try {
      const response = await fetch(`https://apidashboard.eurekagroup.id/candidates/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Remove from local state
      setData(prevData => prevData.filter(candidate => candidate.id !== id && candidate._id !== id));
      
      SwalConfig.success(
        'Berhasil Dihapus!',
        'Data kandidat telah berhasil dihapus dari sistem.',
        2000
      );
    } catch (error) {
      console.error('Error deleting candidate:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal menghapus kandidat: ${error.message}`
      });
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchCandidates();
  }, []);

  // Handle status filter change
  useEffect(() => {
    if (statusFilter) {
      fetchCandidatesByStatus(statusFilter);
    } else {
      fetchCandidates();
    }
  }, [statusFilter]);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handlePositionFilterChange = (event) => {
    setPositionFilter(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (id) => {
    SwalConfig.deleteConfirm('kandidat ini').then((result) => {
      if (result.isConfirmed) {
        deleteCandidate(id);
      }
    });
  };

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'pending':
      case 'new':
        return 'warning';
      case 'progress':
      case 'active': // backend returns 'Active' but we show as Progress
        return 'info';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const canonicalStatus = (s) => {
    if (!s) return '';
    const v = String(s).toLowerCase();
    // Map backend status to frontend canonical status
    if (v === 'pending') return 'pending';
    if (v === 'progress') return 'progress';
    // backend uses 'Active' for progress state; normalize it
    if (v === 'active') return 'progress';
    if (v === 'accepted') return 'accepted';
    if (v === 'rejected') return 'rejected';
    return v;
  };

  const statusToBackend = (s) => {
    const v = String(s || '').toLowerCase();
    // Ikuti enum backend sesuai contoh payload
    // New    -> Pending
    // Progress -> Active
    // Accepted -> Accepted
    // Rejected -> Rejected
    if (v === 'new' || v === 'pending') return 'Pending';
    if (v === 'progress') return 'Active';
    if (v === 'accepted') return 'Accepted';
    if (v === 'rejected') return 'Rejected';
    // default: capitalize first letter
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const getInterviewStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'warning';
      case 'Completed':
        return 'info';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleStatusClick = (id, newStatus) => {
    const candidate = data.find(c => c.id === id || c._id === id);
    if (!candidate) return;

    setStatusModal({
      open: true,
      data: {
        id,
        newStatus,
        currentStatus: candidate.applicationStatus,
        candidate
      }
    });
  };

  const handleStatusConfirm = async () => {
    const { data } = statusModal;
    const { id, newStatus, candidate } = data;

    try {
      // gunakan mapping status yang sama seperti contoh payload backend
      const backendStatus = statusToBackend(newStatus);

      const response = await fetch(`https://apidashboard.eurekagroup.id/candidates/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...candidate,
          applicationStatus: backendStatus
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Update local state pakai backendStatus supaya sinkron
      setData(prevData => prevData.map(c => 
        (c.id === id || c._id === id) 
          ? { ...c, applicationStatus: backendStatus }
          : c
      ));

      SwalConfig.success(
        'Status Updated!',
        `Candidate status has been updated to ${newStatus}`,
        2000
      );
    } catch (error) {
      console.error('Error updating candidate status:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Failed to update status: ${error.message}`
      });
    } finally {
      setStatusModal({ open: false, type: '', data: null });
    }
  };

  const filteredData = data
    .filter(candidate => {
      const fullName = (candidate.personalData?.full_name || '').toString();
      const phone = (candidate.personalData?.phone || '').toString();
      const desired = (candidate.applicationSkills?.desired_position || '').toString();
      const q = searchText.toLowerCase();
      const matchesSearch = fullName.toLowerCase().includes(q) || phone.toLowerCase().includes(q) || desired.toLowerCase().includes(q);
      // match using canonical status so 'Active' (backend) is treated as 'progress'
      const matchesStatus = statusFilter === '' || canonicalStatus(candidate.applicationStatus) === canonicalStatus(statusFilter);
      const matchesPosition = positionFilter === '' || desired === positionFilter;

      return matchesSearch && matchesStatus && matchesPosition;
    })
    .sort((a, b) => (a.personalData?.full_name || '').toString().localeCompare((b.personalData?.full_name || '').toString()));

  const uniquePositions = [...new Set(data.map(candidate => candidate.applicationSkills?.desired_position).filter(Boolean))];

  const handleViewTabChange = (event, newValue) => {
    setViewTab(newValue);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleDragStart = (e, id) => {
    // Determine items to drag: either selectedIds if contains id, or single id
    const dragging = selectedIds.has(id) ? Array.from(selectedIds) : [id];
    e.dataTransfer.setData('text/plain', JSON.stringify(dragging));
    // Optionally set drag image
  };

  const [isDragging, setIsDragging] = useState(false);
  const [allowedTargets, setAllowedTargets] = useState(new Set());

  // Timed token helpers (encode/decode safely)
  const encodeToken = (obj) => {
    try {
      const str = JSON.stringify(obj);
      return btoa(unescape(encodeURIComponent(str)));
    } catch (e) {
      return '';
    }
  };

  const generateTimedLink = (id) => {
    const expires = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
    const token = encodeToken({ id, expires });
    return `${window.location.origin}/public/candidate/continue/${id}?t=${token}`;
  };

  const copyPublicLink = async (id) => {
    try {
      const link = generateTimedLink(id);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
        Swal.fire({ ...SwalConfig.success, title: 'Link disalin', text: 'Link publik kandidat telah disalin ke clipboard.' });
      } else {
        // Fallback for older browsers
        // Show prompt to let user copy manually
        // eslint-disable-next-line no-alert
        window.prompt('Salin link publik kandidat (Ctrl/Cmd+C, Enter):', link);
        Swal.fire({ ...SwalConfig.info, title: 'Salin link', text: 'Gunakan Ctrl/Cmd+C lalu Enter untuk menyalin link.' });
      }
    } catch (err) {
      console.error('Gagal menyalin link publik', err);
      Swal.fire({ ...SwalConfig.error, title: 'Gagal', text: 'Tidak dapat menyalin link ke clipboard.' });
    }
  };

  const computeAllowedTargetsForIds = (ids) => {
    const perIdAllowed = ids.map(id => {
      const c = data.find(x => x.id === id || x._id === id);
      const cur = canonicalStatus(c?.applicationStatus || '');
      if (cur === 'new') return new Set(['progress']);
      if (cur === 'progress') return new Set(['accepted', 'rejected']);
      if (cur === 'rejected') return new Set(['new']);
      return new Set(); // accepted or unknown => no moves
    });

    // intersection
    if (perIdAllowed.length === 0) return new Set();
    const intersection = new Set(perIdAllowed[0]);
    for (let i = 1; i < perIdAllowed.length; i++) {
      for (const v of Array.from(intersection)) {
        if (!perIdAllowed[i].has(v)) intersection.delete(v);
      }
    }
    return intersection;
  };

  // enhance drag start to compute allowed targets
  const handleDragStartEnhanced = (e, id) => {
    const dragging = selectedIds.has(id) ? Array.from(selectedIds) : [id];
    e.dataTransfer.setData('text/plain', JSON.stringify(dragging));
    const allowed = computeAllowedTargetsForIds(dragging);
    setAllowedTargets(allowed);
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setAllowedTargets(new Set());
  };

  const updateCandidateStatus = async (id, targetStatus) => {
    const candidate = data.find(c => c.id === id || c._id === id);
    if (!candidate) return { success: false, message: 'Candidate not found' };

    try {
      const backendStatus = statusToBackend(targetStatus);
      const response = await fetch(`https://apidashboard.eurekagroup.id/candidates/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        // Hanya kirim status ke backend sesuai permintaan
        body: JSON.stringify({
          applicationStatus: backendStatus
        })
      });

      if (!response.ok) {
        // Coba baca pesan error dari backend untuk ditampilkan ke user
        let errMsg = `HTTP ${response.status}`;
        try {
          const errJson = await response.json();
          if (errJson && errJson.message) errMsg = errJson.message;
        } catch (e) {
          const text = await response.text();
          if (text) errMsg = text;
        }
        throw new Error(errMsg);
      }

      // Baca response sukses (jika ada) dan update local state
      let respJson = null;
      try {
        respJson = await response.json();
      } catch (e) {
        // jika tidak ada body, biarkan null
      }

      setData(prev => prev.map(c => (c.id === id || c._id === id)
        ? { ...c, applicationStatus: backendStatus }
        : c
      ));

      return {
        success: true,
        data: respJson
      };
    } catch (err) {
      console.error('Error updating status for', id, err);
      return { success: false, message: err.message };
    }
  };

  const handleDrop = async (e, targetStatus) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('text/plain');
    if (!raw) return;
    let ids;
    try { ids = JSON.parse(raw); } catch { ids = [raw]; }

    // Normalize
    ids = ids.map(String);

    // Validate transitions (allow rejected -> pending)
    const invalid = ids.filter(id => {
      const c = data.find(x => x.id === id || x._id === id);
      if (!c) return true;
      const cur = canonicalStatus(c.applicationStatus);
      const tgt = canonicalStatus(targetStatus);
      if (cur === 'pending' && (tgt === 'accepted' || tgt === 'rejected')) return true;
      if (cur === 'accepted') return true; // accepted is final
      if (cur === 'rejected' && tgt !== 'pending') return true; // rejected can only go back to pending
      return false;
    });

    if (invalid.length > 0) {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Invalid Move',
        text: 'Beberapa kandidat tidak dapat dipindahkan langsung ke status yang dipilih. Ikuti alur: New → Progress → Accepted/Rejected.'
      });
      return;
    }

    // Proceed to update each candidate (sequential to keep API rate reasonable)
    const results = [];
    for (const id of ids) {
      const res = await updateCandidateStatus(id, targetStatus);
      results.push({ id, ...res });
    }

    const failed = results.filter(r => !r.success);
    if (failed.length === 0) {
      SwalConfig.success('Updated', 'Status kandidat berhasil diperbarui', 1500);
      // Clear selection
      setSelectedIds(new Set());
    } else {
      const msg = failed[0]?.message || 'Gagal memperbarui sebagian kandidat.';
      Swal.fire({ ...SwalConfig.error, title: 'Partial Failure', text: msg });
    }
  };

  const moveSelectedToStatus = async (targetStatus) => {
    if (!selectedIds || selectedIds.size === 0) {
      Swal.fire({ ...SwalConfig.error, title: 'No Selection', text: 'Pilih kandidat dulu sebelum memindahkan.' });
      return;
    }

    const ids = Array.from(selectedIds);
    // reuse validation from handleDrop
    const invalid = ids.filter(id => {
      const c = data.find(x => x.id === id || x._id === id);
      if (!c) return true;
      const cur = canonicalStatus(c.applicationStatus);
      const tgt = canonicalStatus(targetStatus);
      if (cur === 'new' && (tgt === 'accepted' || tgt === 'rejected')) return true;
      if (cur === 'accepted') return true;
      if (cur === 'rejected' && tgt !== 'new') return true;
      return false;
    });

    if (invalid.length > 0) {
      Swal.fire({ ...SwalConfig.error, title: 'Invalid Move', text: 'Beberapa kandidat tidak dapat dipindahkan langsung ke status yang dipilih.' });
      return;
    }

    const results = [];
    for (const id of ids) {
      const res = await updateCandidateStatus(id, targetStatus);
      results.push({ id, ...res });
    }

    const failed = results.filter(r => !r.success);
    if (failed.length === 0) {
      SwalConfig.success('Updated', 'Status kandidat berhasil diperbarui', 1500);
      setSelectedIds(new Set());
    } else {
      const msg = failed[0]?.message || 'Gagal memperbarui sebagian kandidat.';
      Swal.fire({ ...SwalConfig.error, title: 'Partial Failure', text: msg });
    }
  };

  const handleListStatusChange = async (id, newStatus) => {
    const c = data.find(x => x.id === id || x._id === id);
    if (!c) return;
    const cur = canonicalStatus(c.applicationStatus);
    const tgt = canonicalStatus(newStatus);
    if (cur === 'new' && (tgt === 'accepted' || tgt === 'rejected')) {
      Swal.fire({ ...SwalConfig.error, title: 'Invalid Transition', text: 'Langsung ke Accepted/Rejected tidak diizinkan. Ikuti alur: New → Progress → Accepted/Rejected.' });
      return;
    }
    if (cur === 'accepted') {
      Swal.fire({ ...SwalConfig.error, title: 'Final Status', text: 'Status final tidak dapat diubah.' });
      return;
    }
    if (cur === 'rejected' && tgt !== 'new') {
      Swal.fire({ ...SwalConfig.error, title: 'Invalid Transition', text: 'Kandidat yang ditolak hanya dapat dikembalikan ke New.' });
      return;
    }

    // Konfirmasi sebelum ubah status (seperti di page employee)
    const labelCur = cur ? cur.charAt(0).toUpperCase() + cur.slice(1) : '-';
    const labelTgt = tgt ? tgt.charAt(0).toUpperCase() + tgt.slice(1) : '-';

    const result = await Swal.fire({
      ...SwalConfig.confirm,
      title: 'Ubah Status?',
      html: `Apakah Anda yakin ingin mengubah status kandidat dari <b>${labelCur}</b> menjadi <b>${labelTgt}</b>?`,
      confirmButtonText: 'Ya, Ubah',
      cancelButtonText: 'Batal'
    });

    if (!result.isConfirmed) return;

    const res = await updateCandidateStatus(id, newStatus);
    if (res.success) {
      SwalConfig.success('Updated', 'Status kandidat diperbarui', 1200);
    } else {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Gagal Mengubah Status',
        text: res.message || 'Terjadi kesalahan saat mengubah status kandidat.'
      });
    }
  };

  return (
    <div className="animate-fade-in">
      <StatusConfirmationModal 
        open={statusModal.open}
        onClose={() => setStatusModal({ open: false, type: '', data: null })}
        onConfirm={handleStatusConfirm}
        title={`Update ${statusModal.type === 'application' ? 'Application' : 'Interview'} Status`}
        message={`Are you sure you want to change the ${statusModal.type} status for this candidate?`}
        currentStatus={statusModal.data?.currentStatus || ''}
        newStatus={statusModal.data?.newStatus || ''}
      />
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Kandidat
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola data kandidat dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<RefreshIcon />} 
              onClick={fetchCandidates}
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />} 
              onClick={() => navigate('/kontak/candidate/add')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah Kandidat
            </Button>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <Divider />
      <CardContent>
        {/* Filter Section */}
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Cari Kandidat"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterIcon color="action" />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }}
                >
                  <MenuItem value="">
                    <em>Semua Status</em>
                  </MenuItem>
                  <MenuItem value="Pending">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DotIcon sx={{ fontSize: 12, color: '#ff9800' }} />
                      Pending
                    </Box>
                  </MenuItem>
                  <MenuItem value="Progress">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DotIcon sx={{ fontSize: 12, color: '#2196f3' }} />
                      Progress
                    </Box>
                  </MenuItem>
                  <MenuItem value="Accepted">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DotIcon sx={{ fontSize: 12, color: '#4caf50' }} />
                      Accepted
                    </Box>
                  </MenuItem>
                  <MenuItem value="Rejected">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DotIcon sx={{ fontSize: 12, color: '#f44336' }} />
                      Rejected
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={positionFilter}
                  onChange={handlePositionFilterChange}
                  displayEmpty
                  sx={{
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }}
                >
                  <MenuItem value="">
                    <em>Semua Posisi</em>
                  </MenuItem>
                  {uniquePositions.map(position => (
                    <MenuItem key={position} value={position}>
                      {position}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {filteredData.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Kandidat
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table / Kanban Switcher */}
        <Tabs value={viewTab} onChange={handleViewTabChange} sx={{ mb: 2 }}>
          <Tab label="List" />
          <Tab label="Kanban" />
        </Tabs>

        {viewTab === 0 ? (
          <Box sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400, flexDirection: 'column', gap: 2 }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>Memuat data kandidat...</Typography>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>No</TableCell>
                        <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem', minWidth: 200 }}>Kandidat</TableCell>
                        <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Posisi Dilamar</TableCell>
                        <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Gaji Diharapkan</TableCell>
                        <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>Status</TableCell>
                        <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.875rem', textAlign: 'center' }}>Aksi</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((candidate, index) => (
                        <TableRow key={candidate.id} sx={{ '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', transform: 'scale(1.001)', transition: 'all 0.2s ease' }, '&:nth-of-type(even)': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover' } }}>
                          <TableCell sx={{ fontWeight: 500 }}>{index + 1 + page * rowsPerPage}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>{candidate.personalData?.full_name || '-'}</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{(candidate.personalData?.gender || '-') } • {(candidate.personalData?.age || '-') } tahun</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{candidate.personalData?.phone || '-'}</Typography>
                              </Box>
                                                            <Avatar src={candidate.personalData?.photo} alt={candidate.personalData?.full_name || ''} sx={{ width: 40, height: 40 }} />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={candidate.applicationSkills?.desired_position || '-'} variant="outlined" color="primary" size="small" sx={{ fontWeight: 500 }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              {candidate.applicationSkills?.expected_salary ? `Rp ${parseInt(candidate.applicationSkills.expected_salary.toString().replace(/\./g, '')).toLocaleString('id-ID')}` : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                              <Select
                                size="small"
                                value={canonicalStatus(candidate.applicationStatus)}
                                onChange={(e) => handleListStatusChange(candidate.id, e.target.value)}
                                renderValue={(val) => {
                                  const key = getStatusColor(val);
                                  const label = val ? (val.charAt(0).toUpperCase() + val.slice(1)) : '-';
                                  return (
                                    <Typography component="span" sx={{ color: (theme) => theme.palette[key]?.main || theme.palette.text.primary, fontWeight: 700 }}>{label}</Typography>
                                  );
                                }}
                                sx={{ height: 36 }}
                              >
                                <MenuItem value="new">
                                  <Typography sx={{ color: (theme) => theme.palette.warning.main, fontWeight: 700 }}>New</Typography>
                                </MenuItem>
                                <MenuItem value="progress">
                                  <Typography sx={{ color: (theme) => theme.palette.info.main, fontWeight: 700 }}>Progress</Typography>
                                </MenuItem>
                                <MenuItem value="accepted">
                                  <Typography sx={{ color: (theme) => theme.palette.success.main, fontWeight: 700 }}>Accepted</Typography>
                                </MenuItem>
                                <MenuItem value="rejected">
                                  <Typography sx={{ color: (theme) => theme.palette.error.main, fontWeight: 700 }}>Rejected</Typography>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <IconButton size="small" onClick={() => navigate(`/kontak/candidate/view/${candidate.id}`)} sx={{ color: 'primary.main' }}><VisibilityIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => navigate(`/kontak/candidate/edit/${candidate.id}`)} sx={{ color: 'warning.main' }}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => copyPublicLink(candidate.id)} sx={{ color: 'info.main' }}><OpenInNewIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => handleDeleteClick(candidate.id)} sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} sx={{ borderTop: '1px solid', borderColor: 'grey.200', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }} />
              </>
            )}
          </Box>
        ) : (
          // Kanban View
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/** compute groups on render - pending, progress, accepted, rejected **/}
            {['pending', 'progress', 'accepted', 'rejected'].map((statusKey) => (
              <Paper
                key={statusKey}
                variant="outlined"
                sx={{
                  flex: 1,
                  p: 1,
                  minHeight: 400,
                  bgcolor: (theme) => statusKey === 'pending' ? (theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.25)' : '#f7fbff') : 'background.paper',
                  border: isDragging ? (allowedTargets.has(statusKey) ? '2px dashed' : '1px dashed rgba(0,0,0,0.08)') : undefined,
                  borderColor: isDragging ? (allowedTargets.has(statusKey) ? 'primary.main' : 'grey.300') : undefined
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, statusKey)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>{statusKey}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip label={filteredData.filter(d => canonicalStatus(d.applicationStatus) === statusKey).length} size="small" />
                    <Tooltip title="Pindahkan kandidat terpilih ke status ini">
                      <Button size="small" variant="outlined" onClick={() => moveSelectedToStatus(statusKey)}>Move</Button>
                    </Tooltip>
                  </Box>
                </Box>
                <Box>
                  {(filteredData.filter(d => canonicalStatus(d.applicationStatus) === statusKey)).map((cand) => (
                      <Paper key={cand.id} draggable onDragStart={(e) => handleDragStartEnhanced(e, cand.id)} onDragEnd={handleDragEnd} sx={{ p: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'grab' }}>
                      <Checkbox size="small" checked={selectedIds.has(cand.id)} onChange={() => handleToggleSelect(cand.id)} />
                      <Avatar src={cand.personalData?.photo} sx={{ width: 36, height: 36 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{cand.personalData?.full_name || '-'}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{cand.applicationSkills?.desired_position || '-'} • Rp {parseInt(((cand.applicationSkills?.expected_salary) || '0').toString().replace(/\./g, '')).toLocaleString('id-ID')}</Typography>
                      </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" onClick={() => copyPublicLink(cand.id)} sx={{ color: 'info.main' }}><OpenInNewIcon fontSize="small" /></IconButton>
                          <Chip label={canonicalStatus(cand.applicationStatus).charAt(0).toUpperCase() + canonicalStatus(cand.applicationStatus).slice(1)} color={getStatusColor(canonicalStatus(cand.applicationStatus))} size="small" />
                        </Box>
                    </Paper>
                  ))}
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
    <StatusConfirmationModal 
      open={statusModal.open}
      onClose={() => setStatusModal({ open: false, data: null })}
      onConfirm={handleStatusConfirm}
      title="Update Status"
      message="Are you sure you want to change the status for this candidate?"
      currentStatus={statusModal.data?.currentStatus || ''}
      newStatus={statusModal.data?.newStatus || ''}
    />
    </div>
  );
};

export default CandidatePage;
